import { z } from 'zod';
import { cellRowTypeSchema, cellSchema } from './cell.js';
import {
  matrixGenerationJobStatusSchema,
  matrixGenerationPromptSchema,
} from './matrix-generation.js';
import { palierNumberSchema } from './palier.js';

/** See docs/design/matrice-creation-wizard/2026-08-14-matrix-regeneration-design.md, I1/I3. */
export const regenerationScopeSchema = z.enum([
  'cell',
  'row',
  'column',
  'matrix',
  'multiCell',
  'activationBlock',
]);
export type RegenerationScope = z.infer<typeof regenerationScopeSchema>;

/**
 * Discriminated on `scope` — mirrors `regeneration_batches.scope_selector`'s
 * jsonb shape. `matrix` carries no extra data (everything is in scope).
 */
export const regenerationScopeSelectorSchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('matrix') }),
  z.object({ scope: z.literal('activationBlock') }),
  z.object({ scope: z.literal('row'), rowType: cellRowTypeSchema, rowRef: z.string().min(1) }),
  z.object({ scope: z.literal('column'), columnNumber: palierNumberSchema }),
  z.object({ scope: z.literal('cell'), cellId: z.string().uuid() }),
  z.object({ scope: z.literal('multiCell'), cellIds: z.array(z.string().uuid()).min(1) }),
]);
export type RegenerationScopeSelector = z.infer<typeof regenerationScopeSelectorSchema>;

export const regenerationBatchSchema = z.object({
  id: z.string().uuid(),
  matrixId: z.string().uuid(),
  scope: regenerationScopeSchema,
  scopeSelector: regenerationScopeSelectorSchema,
  comment: z.string().nullable(),
  status: matrixGenerationJobStatusSchema,
  error: z.string().nullable(),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
});
export type RegenerationBatch = z.infer<typeof regenerationBatchSchema>;

/** Optional everywhere (P4) — an empty comment tells the scope prompt to use its own judgment. */
const regenerationCommentSchema = z.string().max(4000).optional();

/**
 * What joins the five pieces of a regeneration prompt (scope prompt, composed
 * context, coherence context, comment section, output instructions).
 *
 * Exported rather than written out at each site because the front now
 * concatenates the two halves it renders — the editable body and the locked
 * instructions tail — and what it submits has to be byte-identical to what the
 * server would have assembled. A separator that differed by one newline would
 * make every prompt "edited by hand" and every server-side contract check a
 * near-miss.
 */
export const REGENERATION_PROMPT_SEPARATOR = '\n\n';

/**
 * Ceiling on an operator-supplied regeneration prompt (issue #6).
 *
 * Its own cap, far above the comment's 4000: the comment is one paragraph a
 * user types, while this carries the whole assembled prompt — scope prompt,
 * company/setup/palier context, the coherence context for the scope, the
 * comment, and the JSON contract. A matrix-scope prompt on a populated grid
 * already runs to ~25 000 characters, and a full-grid coherence context grows
 * with the cells it quotes, so the margin here is deliberately wide: refusing
 * a legitimate prompt at submit time, after the operator has edited it, would
 * lose their work for no safety gain.
 */
export const REGENERATION_PROMPT_MAX_CHARS = 200_000;

/** The full prompt an operator edited in the drawer, submitted in place of the server's own assembly (issue #6). */
const regenerationPromptOverrideSchema = z.string().max(REGENERATION_PROMPT_MAX_CHARS).optional();

/**
 * The heading that opens the comment section inside an assembled prompt.
 *
 * Shared rather than duplicated because the drawer keeps the Commentaire field
 * and the prompt text in sync in both directions, and it finds the section by
 * this heading. If the backend's wording and the frontend's anchor ever
 * diverged, the field would silently stop tracking the prompt — the exact
 * failure the "section introuvable" state is designed to make visible.
 */
export const REGENERATION_COMMENT_HEADING = "## Commentaire de l'utilisateur";

/** What the section says when no comment was given (P4/P5) — the prompt still has to tell the model what to do with the silence. */
export const REGENERATION_EMPTY_COMMENT_TEXT =
  '(aucun commentaire fourni — utilisez votre jugement, dans le respect des contraintes de cohérence ci-dessus)';

/** The comment section exactly as it appears in an assembled prompt — used by the runner to build it and by the drawer to rewrite it in place. */
export function buildRegenerationCommentSection(comment: string | null | undefined): string {
  return [
    REGENERATION_COMMENT_HEADING,
    '',
    comment?.trim() ? comment : REGENERATION_EMPTY_COMMENT_TEXT,
  ].join('\n');
}

/** `POST /matrices/:id/regenerate-matrix` */
export const regenerateMatrixInputSchema = z.object({
  comment: regenerationCommentSchema,
  prompt: regenerationPromptOverrideSchema,
});
export type RegenerateMatrixInput = z.infer<typeof regenerateMatrixInputSchema>;

/** `POST /matrices/:id/regenerate-activation-block` */
export const regenerateActivationBlockInputSchema = z.object({
  comment: regenerationCommentSchema,
  prompt: regenerationPromptOverrideSchema,
});
export type RegenerateActivationBlockInput = z.infer<typeof regenerateActivationBlockInputSchema>;

/** `POST /matrices/:id/regenerate-row` */
export const regenerateRowInputSchema = z.object({
  rowType: cellRowTypeSchema,
  rowRef: z.string().min(1),
  comment: regenerationCommentSchema,
  prompt: regenerationPromptOverrideSchema,
});
export type RegenerateRowInput = z.infer<typeof regenerateRowInputSchema>;

/** `POST /matrices/:id/regenerate-column` */
export const regenerateColumnInputSchema = z.object({
  columnNumber: palierNumberSchema,
  comment: regenerationCommentSchema,
  prompt: regenerationPromptOverrideSchema,
});
export type RegenerateColumnInput = z.infer<typeof regenerateColumnInputSchema>;

/** `POST /matrices/:id/regenerate-cell` — references an existing cell by id (I3). */
export const regenerateCellInputSchema = z.object({
  cellId: z.string().uuid(),
  comment: regenerationCommentSchema,
  prompt: regenerationPromptOverrideSchema,
});
export type RegenerateCellInput = z.infer<typeof regenerateCellInputSchema>;

/** `POST /matrices/:id/regenerate-cells` — arbitrary multi-cell selection (M11). */
export const regenerateCellsInputSchema = z.object({
  cellIds: z.array(z.string().uuid()).min(1),
  comment: regenerationCommentSchema,
  prompt: regenerationPromptOverrideSchema,
});
export type RegenerateCellsInput = z.infer<typeof regenerateCellsInputSchema>;

/**
 * `POST /matrices/:id/composed-prompt` (issue #6) — the prompt a regeneration
 * *would* send, for the drawer to reveal and let a `pms_admin` edit.
 *
 * POST rather than a second GET: a `multiCell` selector carries an array of
 * UUIDs and the comment runs to 4000 characters, which no query string holds.
 * The GET on the same path is the wizard's récapitulatif preview and is
 * deliberately separate — it composes the runner prompt and the context only
 * (no documents, no output contract) and knows nothing about scopes.
 */
export const regenerationPromptPreviewInputSchema = z.object({
  selector: regenerationScopeSelectorSchema,
  comment: regenerationCommentSchema,
});
export type RegenerationPromptPreviewInput = z.infer<typeof regenerationPromptPreviewInputSchema>;

/**
 * Returned split rather than whole, because the two halves are treated
 * differently on screen: `body` is editable, `instructions` is the JSON output
 * contract and is rendered locked in place. Joining them with
 * `REGENERATION_PROMPT_SEPARATOR` reproduces exactly what the server would
 * have sent.
 */
export const regenerationPromptPreviewSchema = z.object({
  body: z.string(),
  instructions: z.string(),
  scopePromptName: z.string(),
  scopePromptVersion: z.number().int(),
});
export type RegenerationPromptPreview = z.infer<typeof regenerationPromptPreviewSchema>;

/**
 * One past regeneration batch, as the Cadrage tab's Prompt section reads it
 * (issue #6) — the counterpart of `MatrixGenerationPrompt` for the six
 * regeneration scopes.
 *
 * Before this, the section told the reader in as many words that regeneration
 * prompts were not kept. That sentence was true and is now false: a prompt the
 * operator can freely edit makes a cell's content otherwise unexplainable, so
 * every batch records what it sent.
 */
export const regenerationPromptRecordSchema = z.object({
  batchId: z.string().uuid(),
  scope: regenerationScopeSchema,
  status: matrixGenerationJobStatusSchema,
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
  comment: z.string().nullable(),
  /** Null for batches that ran before the column existed, and for one that failed before it got as far as assembling. */
  prompt: z.string().nullable(),
  /** The Langfuse prompt the scope resolved to, e.g. `mss-creator-regen-column`. Null alongside `prompt`. */
  scopePromptName: z.string().nullable(),
  scopePromptVersion: z.number().int().nullable(),
  /**
   * True when what was sent differed from the assembly the server built for
   * this very batch.
   *
   * Recorded at launch, where the comparison is exact and free, rather than
   * derived at read time: recomposing the "expected" prompt now would drift
   * the moment anyone edits the sujet, a palier or a cell, and would start
   * badging batches nobody ever touched.
   */
  promptEdited: z.boolean(),
});
export type RegenerationPromptRecord = z.infer<typeof regenerationPromptRecordSchema>;

/**
 * `GET /matrices/:id/generation-prompts` — both halves of a matrix's prompt
 * history in one response.
 *
 * One route rather than two (issue #6): the Cadrage tab shows a single
 * chronology, and a second endpoint would mean a second loading state, a
 * second 403 path and two ways to be half-loaded on screen.
 */
export const matrixPromptHistorySchema = z.object({
  generations: z.array(matrixGenerationPromptSchema),
  regenerations: z.array(regenerationPromptRecordSchema),
});
export type MatrixPromptHistory = z.infer<typeof matrixPromptHistorySchema>;

/**
 * `POST /regeneration-batches/:id/restore` (I2) — non-destructive, like
 * per-cell restore: inserts new `cell_versions` rows for every cell that's
 * still on the batch's own version, skipping any whose current version is a
 * manual edit newer than the batch (so a later manual fix is never silently
 * clobbered by an older regeneration).
 */
export const restoreRegenerationBatchResultSchema = z.object({
  restoredCells: z.array(cellSchema),
  skippedCellIds: z.array(z.string().uuid()),
});
export type RestoreRegenerationBatchResult = z.infer<typeof restoreRegenerationBatchResultSchema>;
